// Cálculo de Aguinaldo (CR — Ley 2412 / Código de Trabajo Art. 12)
// Base imponible: salarios ordinarios y extraordinarios percibidos entre el 01-dic del
// año anterior y el 30-nov del año en curso. El monto a pagar = total / 12.
// Se entrega en los primeros 20 días de diciembre.

import { subPeriodsForMonth, buildOverrideFromDoc } from "./subperiods";

const MES_NOMBRES = [
  "Enero","Febrero","Marzo","Abril","Mayo","Junio",
  "Julio","Agosto","Septiembre","Octubre","Noviembre","Diciembre",
];

// Devuelve los 12 meses del período de aguinaldo en orden cronológico:
// Diciembre (año−1), Enero..Noviembre (año actual)
export function mesesAguinaldo(year) {
  const meses = [{ year: year - 1, month: 11, label: "Diciembre", anio: year - 1 }];
  for (let m = 0; m < 11; m++) {
    meses.push({ year, month: m, label: MES_NOMBRES[m], anio: year });
  }
  return meses;
}

export function calcularAguinaldo(empleado, comprobantes = [], year) {
  const meses = mesesAguinaldo(year);
  // Excluimos los comprobantes que sean a su vez aguinaldos para evitar
  // que se sumen como parte de la base imponible en ejercicios futuros.
  const propios = comprobantes.filter(c => c.empleado_id === empleado.id && c.kind !== "aguinaldo");
  const desglose = meses.map(m => {
    // Mes acumula los comprobantes cuya fecha_inicio cae en ese mes/año
    const total = propios
      .filter(c => {
        const f = (c.fecha_inicio || c.fecha_fin || "").slice(0, 7);
        const ym = `${m.year}-${String(m.month + 1).padStart(2, "0")}`;
        return f === ym;
      })
      .reduce((s, c) => s + (Number(c.monto_bruto) || 0), 0);
    return { ...m, salario: total };
  });
  const totalSalarios = desglose.reduce((s, r) => s + r.salario, 0);
  const aguinaldo = totalSalarios / 12;
  return { desglose, totalSalarios, aguinaldo, periodoInicio: `${year - 1}-12-01`, periodoFin: `${year}-11-30` };
}

// ============================================================================
// Cálculo LIVE de aguinaldo desde los datos de nómina (asistencia + rebajos +
// bonificaciones + overrides manuales + empresa). No depende de que se hayan
// descargado los comprobantes de cada mes — el bruto de cada mes se calcula
// al vuelo usando exactamente la misma lógica que la Planilla Mensual,
// desglosado por sub-período (semanal / quincenal / mensual) y aplicando los
// overrides manuales guardados en la colección `planilla_overrides`.
//
// Cambios en asistencia, bonificaciones u overrides se reflejan automáticamente.
//
// Params:
//   empleado       — empleado
//   allAsistencia  — todas las asistencias del período de aguinaldo (12 meses)
//   allRebajos     — todos los rebajos
//   allBonif       — todas las bonificaciones
//   allOverrides   — todos los overrides manuales de planilla para el período
//   empresa        — configuración de empresa (puestos, actividad, etc.)
//   year           — año del aguinaldo (base = dic year-1 → nov year)
//   computePayrollForEmployee — función importada de payroll.js (evita ciclo)
//   puestoNoHorasExtras, puestosMap — helpers de puestos
// ============================================================================
export function calcularAguinaldoLive({
  empleado, allAsistencia, allRebajos, allBonif, allOverrides = [], empresa, year,
  computePayrollForEmployee, puestosMap, puestoNoHorasExtras,
}) {
  const meses = mesesAguinaldo(year);
  const grupoAct = empresa?.actividad_grupo;
  const ccssPensPct = Number(empresa?.ccss_pensionado_pct);
  const ccssPensRate = (isFinite(ccssPensPct) && ccssPensPct > 0) ? ccssPensPct / 100 : null;
  const noHorasExtras = puestoNoHorasExtras(puestosMap, empleado?.puesto);
  const freq = String(empleado.frecuencia_pago || "Mensual").toLowerCase();
  const diasBase = (grupoAct === "servicios_industria" && freq === "semanal") ? 26 : 30;
  const ccssRate = ccssPensRate;

  // Fecha de ingreso: solo se computan salarios devengados a partir de esta
  // fecha. Si el empleado ingresó a mitad del período de aguinaldo, los meses
  // previos deben quedar en ₡0 y el mes de ingreso debe considerar solo los
  // días trabajados desde la fecha_ingreso en adelante.
  const fechaIngreso = (empleado.fecha_ingreso || "").slice(0, 10); // "YYYY-MM-DD"
  const ymIngreso = fechaIngreso ? fechaIngreso.slice(0, 7) : "";   // "YYYY-MM"

  const desglose = meses.map(m => {
    const ym = `${m.year}-${String(m.month + 1).padStart(2, "0")}`;

    // 1) Meses completamente anteriores a fecha_ingreso: salario 0
    if (fechaIngreso && ym < ymIngreso) {
      return { ...m, salario: 0 };
    }

    // 2) Generar sub-períodos del mes según frecuencia del empleado (semanal
    //    Lun-Dom con lunes dentro del mes / quincenal 1-15 y 16-fin / mensual).
    //    Para cada sub-período: filtrar asistencia/rebajos/bonif y buscar el
    //    override manual matching; luego sumar los brutos.
    const periods = subPeriodsForMonth(m.year, m.month, freq);
    let bruto = 0;
    for (const p of periods) {
      const ast = allAsistencia.filter(a => {
        if (a.empleado_id !== empleado.id) return false;
        const f = (a.fecha || "").slice(0, 10);
        if (!f || f < p.start || f > p.end) return false;
        if (fechaIngreso && f < fechaIngreso) return false;
        return true;
      });
      const rebs = allRebajos.filter(r => {
        if (r.empleado_id !== empleado.id || !r.activo) return false;
        const f = (r.fecha || r.fecha_inicio || "").slice(0, 10);
        if (!f || f < p.start || f > p.end) return false;
        if (fechaIngreso && f < fechaIngreso) return false;
        return true;
      });
      const bons = allBonif.filter(b => {
        if (b.empleado_id !== empleado.id || !b.activo) return false;
        const f = (b.fecha || "").slice(0, 10);
        if (!f || f < p.start || f > p.end) return false;
        if (fechaIngreso && f < fechaIngreso) return false;
        return true;
      });
      const ovDoc = allOverrides.find(x =>
        x.empleado_id === empleado.id &&
        x.fecha_inicio === p.start &&
        x.fecha_fin === p.end
      );
      const override = buildOverrideFromDoc(ovDoc);
      const calc = computePayrollForEmployee(empleado, ast, {
        rebajos: rebs, bonificaciones: bons,
        extrasDoblesFeriado: !!empresa.extras_dobles_feriado,
        ccssAplicaExtras: !!empresa.ccss_aplica_extras,
        noHorasExtras, diasBase, ccssRate, override,
        periodStart: p.start, periodEnd: p.end,
        jornada48: empresa?.actividad_grupo === "servicios_jornadas_especiales" && freq !== "semanal",
      });
      bruto += Number(calc.totalBruto) || 0;
    }
    return { ...m, salario: bruto };
  });
  const totalSalarios = desglose.reduce((s, r) => s + r.salario, 0);
  const aguinaldo = totalSalarios / 12;
  return { desglose, totalSalarios, aguinaldo, periodoInicio: `${year - 1}-12-01`, periodoFin: `${year}-11-30` };
}
